import { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { userInformationAtom } from '../../../Services/_states';
import { useOrgNotificationsSocket } from '../../../shared/sockets';
import { storeNotification } from '../../../utils/notificationStorage.ts';
import { leadsActionSelector } from '../../../Services/_states/Leads/leadSelector.ts';
import { useWebSocketAiContext } from '../../../utils/WebSocketContext.tsx';
import useRefreshUserSession from '../../../hooks/useRefreshUserSession';
import { playNotificationSound } from '../../../components/InterveneDashboard/notificationSound.ts';
import MachadaloLogoTop from '../../../components/Header/header-machadalo-logo.png';
import TestLogo from '../../../components/Header/synergytop-logo.webp';

const DefaultLogo =
  process.env.REACT_APP_NODE_ENV === 'test' ? TestLogo : MachadaloLogoTop;

export default function useAppHeader() {
  const { dispatch } = useWebSocketAiContext();
  const setLeadActions = useSetRecoilState(leadsActionSelector);
  const [unreadMessageCounts, setUnreadMessageCounts] = useState({});
  const [logo, setLogo] = useState(DefaultLogo);
  const [userInfo] = useRecoilState(userInformationAtom);
  const refreshUserSession = useRefreshUserSession();

  const organisationId = userInfo?.profile?.organisation?.organisation_id ?? '';
  const { connectionStatus: status, lastMessage: message, sendMessage } =
    useOrgNotificationsSocket();

  const fetchUserInfo = useCallback(
    async (userId) => {
      try {
        await refreshUserSession(userId);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    },
    [refreshUserSession]
  );

  useEffect(() => {
    if (status === 'Connected' && organisationId) {
      try {
        sendMessage({
          type: 'register',
          organisation_id: organisationId,
          user_id: Number(userInfo?.id ?? 0),
        });
      } catch (err) {
        console.warn(err, 'error');
      }
    }
  }, [status, organisationId, sendMessage, userInfo?.id]);

  useEffect(() => {
    if (!message) return;

    const notificationType =
      message?.type === 'notification'
        ? message.notification_type
        : message?.notification_type;

    if (notificationType === 'unread_message_counts' && message?.data?.totalUnread) {
      setUnreadMessageCounts(message?.data);
    } else if (notificationType === 'new_leads' && message?.data) {
      const entries = Array.isArray(message.data) ? message.data : [message.data];
      entries.forEach((entry) => {
        if (entry?.lead_id && entry?.leads_form_id != null) {
          storeNotification(entry.leads_form_id, entry.lead_id);
        }
        setLeadActions({ type: 'add', payload: entry });
      });
    } else if (notificationType === 'update_leads' && message?.data) {
      setLeadActions({ type: 'update', payload: message?.data });
    } else if (notificationType === 'delete_leads' && message?.data) {
      setLeadActions({ type: 'delete', payload: message?.data });
    } else if (
      notificationType === 'bulk_update_leads' &&
      message?.data &&
      Array.isArray(message?.data)
    ) {
      setLeadActions({ type: 'bulk_update', payload: message?.data });
    } else if (notificationType === 'ai_query_response') {
      dispatch({
        type: 'AI_QUERY_RESPONSE',
        payload: message?.data ?? null,
      });
    } else if (notificationType === 'image_generation_completed') {
      dispatch({
        type: 'IMAGE_GENERATION_COMPLETED',
        payload: message?.data ?? null,
      });
    } else if (notificationType === 'questions_generated') {
      dispatch({
        type: 'QUESTIONS_GENERATED',
        payload: message?.data ?? null,
      });
    } else if (notificationType === 'permissions_updated') {
      fetchUserInfo(userInfo?.id);
    } else if (notificationType === 'new_user_message_initiated') {
      playNotificationSound();
    }
  }, [message, dispatch, setLeadActions, fetchUserInfo, userInfo?.id]);

  useEffect(() => {
    try {
      const storedUserInfo = localStorage.getItem('userInfo');
      if (storedUserInfo) {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        const localLogo = parsedUserInfo?.profile?.organisation?.logo_url;
        setLogo(localLogo || DefaultLogo);
      } else {
        setLogo(DefaultLogo);
      }
    } catch (err) {
      console.error('Failed to parse userInfo from localStorage', err);
      setLogo(DefaultLogo);
    }
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      const audio = new Audio();
      audio.play().catch(() => {});
      document.removeEventListener('click', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    return () => {
      document.removeEventListener('click', unlockAudio);
    };
  }, []);

  return {
    logo,
    defaultLogo: DefaultLogo,
    unreadMessageCounts,
    userInfo,
  };
}
